import { css } from '@emotion/react';
import { useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { usePageTitle } from '../../Lib';

import { ApiOrgPropertyFacts, ApiPropertyProject } from '@kaiizen/kai-pro-api';

import { useAutoSwitchOrgForResource, useOrgMarket, useOrgProperty, useProjectTypes, usePropertyProjects } from '@/Client/State';
import { useAuth } from '@kaiizen/kai-pro-api-react-state';

import { PageLayout } from '@/Client/Layout';
import { HeroImageCard, MarketCard, NoRecordsPlaceholder, ProjectPanel, PropertyCard, RecordListHeading } from '@/Client/Partials';
import { LAYOUT2, STRING_CONSTANTS } from '@/Client/Styles';
import { DataTable, FlyOut, MasonryGrid } from '@/Client/Widgets';
import { formatUsd } from '../../Presenters';
import { GenericLoadingPage } from '../GenericPages';

const PROJECT_LIST_CONTAINER_STYLE = css`
  label: PROJECT_LIST_CONTAINER_STYLE;
  max-height: 70vh;
  flex-shrink: 0;
`;

export const PropertyPage = () => {
  const { id } = useParams();
  const { userCan } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orgProperty, propertyMutators, isError, error } = useOrgProperty(id);
  const { orgMarket } = useOrgMarket(orgProperty?.orgMarketId);
  const { getProjectTypeLabel } = useProjectTypes();
  usePageTitle(orgProperty?.address ? `Property · ${orgProperty.address}` : undefined);
  const propertyIsArchived = !!orgProperty?.archivedAt;
  const showArchived = searchParams.get('archived') === 'true';
  const { propertyProjects: activePropertyProjects, propertyProjectMutators } = usePropertyProjects({ orgPropertyId: id, archived: false });
  const { propertyProjects: archivedPropertyProjects } = usePropertyProjects({ orgPropertyId: id, archived: true });
  const { propertyProjects: allPropertyProjects } = usePropertyProjects({ orgPropertyId: id, includeArchived: true, enabled: propertyIsArchived });
  const [showNewProjectFlyout, setShowNewProjectFlyout] = useState(false);
  const propertyProjects = propertyIsArchived ? allPropertyProjects : showArchived ? archivedPropertyProjects : activePropertyProjects;
  const canCreateProject = userCan('create', 'PropertyProject');
  const canUpdateProject = userCan('update', 'PropertyProject');
  const canUpdateProperty = userCan('update', 'OrgProperty');

  useAutoSwitchOrgForResource(
    { error: isError ? error : undefined, orgPropertyId: id },
  );

  if (!id) {
    return <Navigate to='/properties' replace />;
  }
  if (!orgProperty || !propertyProjects) return <GenericLoadingPage />;

  const displayProjects = propertyProjects.map((project: ApiPropertyProject) => ({
    id: project.id,
    createdDate: project.createdAt,
    updatedDate: project.updatedAt,
    projectType: getProjectTypeLabel(project.customerMetadata?.projectType)
      || STRING_CONSTANTS.missingDataString,
    name: project.name || STRING_CONSTANTS.missingDataString,
    description: project.description || STRING_CONSTANTS.missingDataString,
    status: project.archivedAt ? 'archived' : project.status,
    budget: project.customerMetadata?.budget || STRING_CONSTANTS.missingDataString,
    link: `/project/${project.id}`,
    menu: !canUpdateProject ? [] : [
      {
        label: project.archivedAt ? 'Unarchive' : 'Archive',
        action: async () => {
          await propertyProjectMutators.update(project.id, {
            archivedAt: project.archivedAt ? null : new Date().toISOString(),
          });
        },
      },
      {
        label: project.status === 'active' ? 'Complete Project' : 'Activate Project',
        action: async () => {
          await propertyProjectMutators.update(project.id, {
            status: project.status === 'active' ? 'completed' : 'active',
          });
        },
      },
    ],
  }));

  const haveProjects = displayProjects.length > 0;

  return (
    <PageLayout
      title={`Property: ${orgProperty?.address || ''}${orgMarket ? ` (Market: ${orgMarket.name})` : ''}${propertyIsArchived ? ' — Archived' : ''}`}
      contentLayoutStyling={'standard'}
    >
      <MasonryGrid minColumnWidth={350} backgroundColor='var(--background)' padding={LAYOUT2.spacing2 + 'px'}>
        <HeroImageCard orgPropertyId={orgProperty?.id} aspectRatio={1.41218638} />
        <PropertyCard
          extraFields
          noLinkButton
          orgProperty={orgProperty}
          editable={canUpdateProperty}
          updatePropertyFact={canUpdateProperty
            ? (updates: Partial<ApiOrgPropertyFacts>) =>
              propertyMutators.update(orgProperty.id, { customerMetadata: { ...orgProperty.customerMetadata, ...updates } })
            : undefined}
        />
        <MarketCard orgMarket={orgMarket} orgMarketId={orgProperty?.orgMarketId as string | undefined} />
      </MasonryGrid>
      <RecordListHeading
        recordType='Project'
        activeCount={activePropertyProjects?.length ?? 0}
        archivedCount={archivedPropertyProjects?.length ?? 0}
        showArchived={showArchived}
        displayingAllArchiveStatuses={propertyIsArchived}
        displayCountOverride={propertyIsArchived ? displayProjects.length : undefined}
        onCreate={canCreateProject ? () => setShowNewProjectFlyout(true) : undefined}
        onToggleArchived={() => {
          const newParams = new URLSearchParams(searchParams);
          if (showArchived) {
            newParams.delete('archived');
          } else {
            newParams.set('archived', 'true');
          }
          setSearchParams(newParams);
        }}
      />
      {!haveProjects
        ? (
          <NoRecordsPlaceholder
            recordType='Projects'
            qualifier={showArchived ? 'Archived' : undefined}
            description={canCreateProject ? 'Create your first project to get started.' : 'No projects have been created yet.'}
            createButtonText={canCreateProject ? 'Add a Project' : undefined}
            createFirstRecord={canCreateProject
              ? () => {
                setShowNewProjectFlyout(true);
              }
              : undefined}
          />
        )
        : (
          <>
            <div css={PROJECT_LIST_CONTAINER_STYLE}>
              <DataTable
                recordType='project'
                items={displayProjects}
                defaultOrder='desc'
                defaultOrderBy='updatedDate'
                columns={[
                  { name: 'createdDate', sortable: true, sortMethod: 'date', width: '140px', format: (value: string) => new Date(value).toLocaleDateString() },
                  { name: 'updatedDate', sortable: true, sortMethod: 'date', width: '140px', format: (value: string) => new Date(value).toLocaleDateString() },
                  { name: 'projectType', sortable: true, width: '120px' },
                  { name: 'name', displayName: 'Project Name', sortable: true, width: '200px' },
                  { name: 'description', sortable: true, truncate: true, width: '250px' },
                  { name: 'budget', sortable: true, width: '120px', format: (value: number) => formatUsd(value) },
                  { name: 'status', sortable: true, width: '120px' },
                ]}
              />
            </div>
          </>
        )}
      <FlyOut
        open={showNewProjectFlyout}
        flyOutClosed={() => setShowNewProjectFlyout(false)}
        title='Create New Project'
      >
        <ProjectPanel
          property={orgProperty}
          projectMutators={propertyProjectMutators}
        />
      </FlyOut>
    </PageLayout>
  );
};
